import { jsx, jsxs } from "react/jsx-runtime";
import { ModalDialog, Box, Paragraph, Anchor, Heading, Grid, IconUserOutline, IconUser, IconHouseOutline, IconHouse, IconTeamOutline, IconTeam, Button, IconArrowRightOutline, IconCheckmarkCircleOutline, IconNewTeamMemberOutline, IconDownloadOutline, IconSchoolOutline, TextButton, Alert, IconBulbOutline } from "@zillow/constellation";
import { event } from "@zillow/universal-analytics";
import InputMask from "inputmask";
import { useState, useEffect } from "react";
import { useCurrentUser } from "../../hooks/user.esm.js";
import { UncontrolledInputField, UncontrolledFieldSet } from "../UncontrolledFields/index.esm.js";
import ArrowBlockButton from "./ArrowBlockButton.esm.js";
import FormStepper, { INVISIBLE_SX } from "./FormStepper.esm.js";
import OverlappingStack from "./OverlappingStack.esm.js";
import { usePAFormStepperFieldRefs } from "./PAFormStepperModalHooks.esm.js";
import Tile from "./Tile.esm.js";
import { trackModalDialog, trackStepImpression, trackTileSelect, trackTileImpression, trackInputChange, trackArrowButtonClick, trackBack, trackTileLink, trackContinue } from "./clickstream.esm.js";
import { getNameError, getEmailError, getPhoneError, getOrgError } from "./utils.esm.js";
import { useBlockEnvironment } from "../../contexts/BlockEnvironment/BlockEnvironmentProvider.esm.js";
function PAFormStepperModal() {
  const {
    environment
  } = useBlockEnvironment();
  const [visibleStepName, setVisibleStepName] = useState("welcome");
  const [selectedWelcomeTile, setSelectedWelcomeTile] = useState("agent");
  const homeLinks = [{
    label: "Search for a home",
    href: "https://www.zillow.com/"
  }, {
    label: "List your home for free",
    href: "https://www.zillow.com/for-sale-by-owner/"
  }, {
    label: "See your home's estimated value",
    href: "https://www.zillow.com/how-much-is-my-home-worth/"
  }, {
    label: "Find a real estate agent",
    href: "https://www.zillow.com/professionals/real-estate-agent-reviews/"
  }, {
    label: "Help",
    href: "https://zillow.zendesk.com/hc/en-us/"
  }];
  const otherLinks = [{
    label: "I'm a lender or loan officer",
    href: "https://www.zillow.com/advertising/mortgage/"
  }, {
    label: "I'm a property manager",
    href: "https://www.zillow.com/rentals-network/rental-advertising/"
  }, {
    label: "I'm a brand advertiser",
    href: "https://www.zillow.com/brands/"
  }, {
    label: "Help",
    href: "https://zillow.zendesk.com/hc/en-us/"
  }];
  const [submitting, setSubmitting] = useState(false);
  const {
    nameField,
    emailField,
    phoneField,
    orgField
  } = usePAFormStepperFieldRefs();
  const [defaultValues, setDefaultValues] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [isOpen, setIsOpen] = useState(false);
  const {
    data: {
      user
    }
  } = useCurrentUser(environment === "prod" ? "production" : "qa");
  useEffect(() => {
    var _a, _b, _c, _d;
    if (!user || typeof user === "boolean") {
      return;
    }
    const nextDefaultValues = {
      name: user.firstName + " " + (user == null ? void 0 : user.lastName),
      email: user.email || "",
      phone: ((_a = user.profile) == null ? void 0 : _a.cellNumber) || ""
    };
    (_b = nameField.current) == null ? void 0 : _b.setValue(nextDefaultValues.name);
    (_c = emailField.current) == null ? void 0 : _c.setValue(nextDefaultValues.email);
    (_d = phoneField.current) == null ? void 0 : _d.setValue(nextDefaultValues.phone);
    setDefaultValues(nextDefaultValues);
  }, [emailField, nameField, phoneField, user]);
  function goToNextStepOrSubmit() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i;
    switch (visibleStepName) {
      case "welcome":
        if ((_a = nameField.current) == null ? void 0 : _a.updateAndGetError()) {
          (_b = nameField.current) == null ? void 0 : _b.focus();
          return;
        }
        trackContinue("welcome", "agent-email");
        handleStepChange("agent-email");
        (_c = emailField.current) == null ? void 0 : _c.focus();
        return;
      case "agent-email":
        if ((_d = emailField.current) == null ? void 0 : _d.updateAndGetError()) {
          (_e = emailField.current) == null ? void 0 : _e.focus();
          return;
        }
        trackContinue("agent-email", "agent-phone");
        handleStepChange("agent-phone");
        (_f = phoneField.current) == null ? void 0 : _f.focus();
        return;
      case "agent-phone":
        if ((_g = phoneField.current) == null ? void 0 : _g.updateAndGetError()) {
          (_h = phoneField.current) == null ? void 0 : _h.focus();
          return;
        }
        trackContinue("agent-phone", "agent-org");
        handleStepChange("agent-org");
        return;
      case "agent-org":
        if ((_i = orgField.current) == null ? void 0 : _i.updateAndGetError()) {
          return;
        }
        return true;
      default:
        throw new Error(`unhandled step: ${visibleStepName}`);
    }
  }
  useEffect(function addSyncOpenWithHashListenerOnMount() {
    function updateIsOpen() {
      if (window.location.hash === "#become-a-premier-agent") {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
    window.addEventListener("hashchange", updateIsOpen);
    updateIsOpen();
    return () => {
      window.removeEventListener("hashchange", updateIsOpen);
    };
  }, []);
  useEffect(function trackModalOpen() {
    if (!isOpen) {
      return;
    }
    trackModalDialog("welcome", true);
  }, [isOpen]);
  function handleStepChange(step) {
    setVisibleStepName(step);
  }
  useEffect(function trackStepImpressionOnStepDisplay() {
    if (!isOpen) {
      return;
    }
    if (selectedWelcomeTile !== "agent") {
      return;
    }
    trackStepImpression(visibleStepName);
  }, [isOpen, visibleStepName, selectedWelcomeTile]);
  return /* @__PURE__ */ jsx(ModalDialog, { size: {
    default: ModalDialog.SIZES.FULL_SCREEN,
    md: ModalDialog.SIZES.LG
  }, scrollDocument: true, dividers: false, isOpen, onClose: () => {
    history.pushState(null, "", location.href.substring(0, location.href.indexOf("#")));
    setIsOpen(false);
    setVisibleStepName("welcome");
    trackModalDialog(visibleStepName, false);
  }, renderHeader: null, renderBody: /* @__PURE__ */ jsxs(FormStepper, { onShouldSubmit: goToNextStepOrSubmit, visibleStepName, onPrepareSubmitData: (data) => {
    const nextData = {
      // Spread all fieldValues in case other fields were added.
      ...data,
      fullName: data.name.trim(),
      Name: data.name.trim(),
      Email: data.email.trim(),
      // Replace all non-numeric characters
      Phone: data.phone.replace(/\D/g, ""),
      Brokerage_Size: data.org,
      Business_Line: "Premier Agent",
      Warm_Lead_Source: "PA-Multistep-Form"
    };
    delete nextData.name;
    delete nextData.email;
    delete nextData.phone;
    delete nextData.org;
    return nextData;
  }, onSubmitSuccess: () => {
    handleStepChange("agent-thank-you");
    event({
      // https://dataportal.zillowgroup.net/event-registry/2048
      envelope: {
        event_type_id: "2048",
        event_type_version_id: "4",
        event_template_id: "126",
        event_template_version_id: "1"
      },
      clickstream_trigger: {
        trigger_type_nm: "interaction",
        trigger_location_nm: "cms",
        trigger_source_nm: "cms_pa_multistep_form",
        trigger_object_nm: "no_trigger_object"
      },
      semantic: {
        semantic_event_nm: "cms_submit"
      },
      cms_info: {
        block_type_cd: "apex_form",
        block_id: "pa_multistep_form",
        form_business_line_txt: "Premier Agent",
        form_warm_lead_source_txt: "PA-Form",
        form_is_valid_ind: true,
        form_invalid_fields_txt: [],
        form_submit_success_ind: true,
        form_server_response_message_txt: ""
      }
    });
    setSubmitting(false);
  }, onSubmitStart: () => setSubmitting(true), onSubmitError: () => {
    var _a;
    (_a = orgField.current) == null ? void 0 : _a.setError("Sorry, submission failed. Please try again soon.");
    event({
      // https://dataportal.zillowgroup.net/event-registry/2048
      envelope: {
        event_type_id: "2048",
        event_type_version_id: "4",
        event_template_id: "126",
        event_template_version_id: "1"
      },
      clickstream_trigger: {
        trigger_type_nm: "interaction",
        trigger_location_nm: "cms",
        trigger_source_nm: "cms_pa_multistep_form",
        trigger_object_nm: "no_trigger_object"
      },
      semantic: {
        semantic_event_nm: "cms_submit"
      },
      cms_info: {
        block_type_cd: "apex_form",
        block_id: "pa_multistep_form",
        form_business_line_txt: "Premier Agent",
        form_warm_lead_source_txt: "PA-Multistep-Form",
        form_is_valid_ind: true,
        form_invalid_fields_txt: [],
        form_submit_success_ind: false,
        form_server_response_message_txt: ""
      }
    });
    setSubmitting(false);
  }, children: [
    /* @__PURE__ */ jsx(FormStepper.Step, { progressBar: selectedWelcomeTile === "agent" && {
      current: 1,
      total: 4
    }, name: "welcome", children: /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(Heading, { level: "5", fontFamily: "serif", textAlign: "center", children: "Who are you?" }),
      /* @__PURE__ */ jsxs(Grid, { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: {
        default: "xs",
        md: "sm"
      }, marginTop: "sm", marginBottom: "lg", children: [
        /* @__PURE__ */ jsx(Tile, { title: "Agent or broker", Icon: IconUser, IconOutline: IconUserOutline, selected: selectedWelcomeTile === "agent", onClick: () => {
          setSelectedWelcomeTile("agent");
          trackTileSelect("Agent or broker");
        } }),
        /* @__PURE__ */ jsx(Tile, { title: "Home Buyer or Seller", Icon: IconHouse, IconOutline: IconHouseOutline, selected: selectedWelcomeTile === "homer", onClick: () => {
          setSelectedWelcomeTile("homer");
          trackTileImpression("homer");
          trackTileSelect("Home Buyer or Seller");
        } }),
        /* @__PURE__ */ jsx(Tile, { title: "Other Professional", Icon: IconTeam, IconOutline: IconTeamOutline, selected: selectedWelcomeTile === "other", onClick: () => {
          setSelectedWelcomeTile("other");
          trackTileImpression("other");
          trackTileSelect("Other Professional");
        } })
      ] }),
      /* @__PURE__ */ jsxs(OverlappingStack, { children: [
        /* @__PURE__ */ jsxs(Box, { sx: selectedWelcomeTile !== "agent" && INVISIBLE_SX, children: [
          /* @__PURE__ */ jsx(PAHeading, { children: "What's your name?" }),
          /* @__PURE__ */ jsx(UncontrolledInputField, { placeholder: "First and last name", type: "text", name: "name", autoComplete: "name", ref: nameField, onBlurAfterChange: () => {
            trackInputChange("name", "First and last name", visibleStepName);
          }, getError: getNameError, formFieldProps: {
            marginBottom: "sm"
          }, defaultValue: defaultValues.name }),
          /* @__PURE__ */ jsx(Button, { onClick: goToNextStepOrSubmit, type: "button", buttonType: "primary", icon: /* @__PURE__ */ jsx(IconArrowRightOutline, {}), iconPosition: "end", fluid: true, marginBottom: "md", children: "Continue" }),
          /* @__PURE__ */ jsxs(Paragraph, { textAlign: "center", children: [
            "A member of our team will reach out for a business consultation or you can call us directly at",
            " ",
            /* @__PURE__ */ jsx(Anchor, { href: "tel:8558850115", children: "855-885-0115" }),
            "."
          ] })
        ] }),
        /* @__PURE__ */ jsxs(Box, { sx: selectedWelcomeTile !== "homer" && INVISIBLE_SX, children: [
          /* @__PURE__ */ jsx(Paragraph, { textAlign: "center", fontType: "bodySmallHeading", marginY: "xs", children: "Please explore any of the following options to continue:" }),
          /* @__PURE__ */ jsx(Box, { children: homeLinks.map((link) => {
            return /* @__PURE__ */ jsx(ArrowBlockButton, { href: link.href, onClick: () => trackArrowButtonClick(link, "homer"), marginBottom: "xs", children: link.label }, link.label);
          }) })
        ] }),
        /* @__PURE__ */ jsxs(Box, { sx: selectedWelcomeTile !== "other" && INVISIBLE_SX, children: [
          /* @__PURE__ */ jsx(Paragraph, { textAlign: "center", fontType: "bodySmallHeading", marginY: "xs", children: "Please explore any of the following options to continue:" }),
          /* @__PURE__ */ jsx(Box, { children: otherLinks.map((link) => {
            return /* @__PURE__ */ jsx(ArrowBlockButton, { href: link.href, marginBottom: "xs", onClick: () => trackArrowButtonClick(link, "other"), children: link.label }, link.label);
          }) })
        ] })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(FormStepper.Step, { progressBar: {
      current: 2,
      total: 4
    }, name: "agent-email", onBack: () => {
      trackBack(visibleStepName, "welcome");
      handleStepChange("welcome");
    }, children: /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(PAHeading, { children: "What's your email?" }),
      /* @__PURE__ */ jsx(UncontrolledInputField, { placeholder: "Email", name: "email", autoComplete: "email", type: "email", ref: emailField, onBlurAfterChange: () => {
        trackInputChange("email", "Email", visibleStepName);
      }, getError: getEmailError, formFieldProps: {
        marginBottom: "xs"
      }, defaultValue: defaultValues.email }),
      /* @__PURE__ */ jsx(Button, { type: "button", onClick: goToNextStepOrSubmit, buttonType: "primary", icon: /* @__PURE__ */ jsx(IconArrowRightOutline, {}), iconPosition: "end", fluid: true, marginBottom: "lg", children: "Continue" }),
      /* @__PURE__ */ jsx(MobileBackButton, { onClick: () => handleStepChange("welcome") }),
      /* @__PURE__ */ jsx(PATipAlert, { message: "1M actual buyers sought a Zillow Premier Agent in 2023 - that's nearly a third of all home buyers." })
    ] }) }),
    /* @__PURE__ */ jsx(FormStepper.Step, { progressBar: {
      current: 3,
      total: 4
    }, name: "agent-phone", onBack: () => {
      trackBack(visibleStepName, "agent-email");
      handleStepChange("agent-email");
    }, children: /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(PAHeading, { children: "What's your phone number?" }),
      /* @__PURE__ */ jsx(UncontrolledInputField, { placeholder: "Phone", type: "tel", name: "phone", autoComplete: "tel", ref: phoneField, onBlurAfterChange: () => {
        trackInputChange("phone", "Phone", visibleStepName);
      }, getError: getPhoneError, formFieldProps: {
        marginBottom: "xs"
      }, defaultValue: defaultValues.phone, onMount: (inputRef) => {
        if (!inputRef.current) {
          return;
        }
        const masker = new InputMask({
          // Phone without extension
          mask: "(999) 999-9999",
          placeholder: "_",
          autoUnmask: true
        });
        masker.mask(inputRef.current);
      } }),
      /* @__PURE__ */ jsx(Button, { type: "button", onClick: goToNextStepOrSubmit, buttonType: "primary", icon: /* @__PURE__ */ jsx(IconArrowRightOutline, {}), iconPosition: "end", fluid: true, marginBottom: "lg", children: "Continue" }),
      /* @__PURE__ */ jsx(MobileBackButton, { onClick: () => handleStepChange("agent-email") }),
      /* @__PURE__ */ jsx(PATipAlert, { message: "Every 4 seconds, Zillow connects a home buyer with a Zillow Premier Agent." })
    ] }) }),
    /* @__PURE__ */ jsx(FormStepper.Step, { progressBar: {
      current: 4,
      total: 4
    }, name: "agent-org", onBack: () => {
      trackBack(visibleStepName, "agent-phone");
      handleStepChange("agent-phone");
    }, children: /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(PAHeading, { children: "What's your organization size?" }),
      /* @__PURE__ */ jsx(Box, { sx: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "$spacing.lg"
      }, children: /* @__PURE__ */ jsx(UncontrolledFieldSet, { name: "org", onChange: () => {
        trackInputChange("org", "What's your organization size?", visibleStepName);
      }, getError: getOrgError, ref: orgField, disabled: submitting, options: [{
        label: "I am an individual agent/broker",
        value: "I am an individual agent/broker"
      }, {
        label: "I lead a team/brokerage of 10 or less people",
        value: "I lead a team/brokerage of 10 or less people"
      }, {
        label: "I lead a brokerage of 11+ people",
        value: "I lead a brokerage of 11+ people"
      }] }) }),
      /* @__PURE__ */ jsx(Button, { type: "submit", buttonType: "primary", disabled: submitting, marginBottom: "lg", fluid: true, children: submitting ? "Submitting" : "Submit" }),
      /* @__PURE__ */ jsxs(Paragraph, { textAlign: "center", children: [
        "or call us directly at",
        " ",
        /* @__PURE__ */ jsx(Anchor, { href: "tel:8558850115", children: "855-885-0115" })
      ] }),
      /* @__PURE__ */ jsx(MobileBackButton, { onClick: () => handleStepChange("agent-phone"), sx: {
        marginBottom: "$spacing.lg",
        marginTop: "$spacing.lg"
      } }),
      /* @__PURE__ */ jsx(Paragraph, { textAlign: "center", fontType: "finePrint", children: "By pressing Submit, you agree that Zillow Group may contact you via phone/text about your inquiry, which may involve the use of automated means. You are not required to consent as a condition of purchasing any goods or services. Message/data rates may apply." })
    ] }) }),
    /* @__PURE__ */ jsx(FormStepper.Step, { name: "agent-thank-you", children: /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(Box, { as: Paragraph, textAlign: "center", marginBottom: "sm", sx: {
        "&&": {
          marginTop: "calc(-1 * $spacing.xl)"
        }
      }, children: /* @__PURE__ */ jsx(IconCheckmarkCircleOutline, { fontColor: "brand", size: "lg" }) }),
      /* @__PURE__ */ jsx(PAHeading, { sx: {
        marginBottom: "$spacing.xs"
      }, children: "Thank you!" }),
      /* @__PURE__ */ jsx(Paragraph, { textAlign: "center", fontType: "bodySmallHeading", marginBottom: "lg", children: "We'll contact you in the next 1-2 business days." }),
      /* @__PURE__ */ jsx(Paragraph, { fontType: "bodySmall", textAlign: "center", marginBottom: "md", children: "In the meantime, explore any of the following next steps:" }),
      /* @__PURE__ */ jsxs(Grid, { display: "grid", gridTemplateColumns: {
        default: "1fr",
        md: "repeat(3, 1fr)"
      }, gap: "sm", marginTop: "sm", marginBottom: "lg", children: [
        /* @__PURE__ */ jsx(Tile, { title: "Create your Zillow Premier Agent profile", IconOutline: IconNewTeamMemberOutline, onClick: () => {
          trackTileLink("Create your Zillow Premier Agent profile", "https://www.zillow.com/myzillow/Profile.htm");
        }, href: "https://www.zillow.com/myzillow/Profile.htm", target: "_blank", fontType: "body" }),
        /* @__PURE__ */ jsx(Tile, { title: "Download the Zillow Premier Agent app", IconOutline: IconDownloadOutline, onClick: () => {
          trackTileLink("Download the Zillow Premier Agent app", "https://premieragent.zillow.com/products/app/");
        }, href: "https://premieragent.zillow.com/products/app/", target: "_blank", fontType: "body" }),
        /* @__PURE__ */ jsx(Tile, { title: "Access training and resources", IconOutline: IconSchoolOutline, onClick: () => {
          trackTileLink("Access training and resources", "https://www.zillow.com/agent-resources/");
        }, href: "https://www.zillow.com/agent-resources/", target: "_blank", fontType: "body" })
      ] }),
      /* @__PURE__ */ jsxs(Paragraph, { textAlign: "center", children: [
        "or call us directly at",
        " ",
        /* @__PURE__ */ jsx(Anchor, { href: "tel:8558850115", children: "855-885-0115" })
      ] })
    ] }) })
  ] }), renderFooter: /* @__PURE__ */ jsxs(Box, { as: Paragraph, fontType: "finePrint", textAlign: "center", sx: {
    width: "100%"
  }, children: [
    "We respect your privacy. See our",
    " ",
    /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillowgroup.com/zg-privacy-policy/", target: "_blank", children: "Privacy Policy" })
  ] }) });
}
const PATipAlert = ({
  message
}) => /* @__PURE__ */ jsx(Alert, { icon: /* @__PURE__ */ jsx(IconBulbOutline, {}), body: /* @__PURE__ */ jsxs(Paragraph, { fontColor: "backgroundDarkBlue", children: [
  /* @__PURE__ */ jsx("strong", { children: "Did you know? " }),
  message
] }), role: "alert" });
const PAHeading = (props) => /* @__PURE__ */ jsx(Box, { as: Paragraph, fontType: {
  default: "h4",
  md: "h3"
}, fontFamily: "serif", textAlign: "center", marginBottom: "md", sx: {
  marginBottom: "$spacing.md",
  ...props.sx
}, role: "heading", children: props.children });
const MobileBackButton = (props) => /* @__PURE__ */ jsx(Box, { sx: {
  marginBottom: "$spacing.lg",
  textAlign: "center",
  ...props.sx
}, children: /* @__PURE__ */ jsx(TextButton, { type: "button", onClick: props.onClick, children: "Go back" }) });
export {
  PAFormStepperModal as default
};
